import { defineStore } from 'pinia';
import { getRcTokens, getRcChains, coboAccount, coboDepositSupported } from '@/api/trade';
import {
  hasAuthSession,
  getUserId,
  getWalletAddress,
  setWalletAddress,
} from '@/utils/auth-token';
import { getAuthGeneration } from '@/utils/auth-teardown';
import { fetchWalletDepositBalances } from '@/utils/wallet-deposit-balances';
import { resolveDepositGeoSession } from '@/utils/deposit-geo-session';
import { applyCoboTransferGeoDefaults } from '@/utils/deposit-cobo-geo-defaults';
import {
  readManualCoboPick,
  resolveCoboPickFromOptions,
  writeManualCoboPick,
} from '@/utils/deposit-cobo-manual-pick';
import { isRequestTimeoutLike, withRetry } from '@/utils/request-retry';

function pickDepositAccounts(res) {
  if (Array.isArray(res?.depositAccounts)) return res.depositAccounts;
  if (Array.isArray(res?.data?.depositAccounts)) return res.data.depositAccounts;
  return [];
}

function pickDepositOptions(res) {
  if (Array.isArray(res?.depositOptions)) return res.depositOptions;
  if (Array.isArray(res?.data?.depositOptions)) return res.data.depositOptions;
  return [];
}

function pickPreferredTransferCurrencyIdx(currencies) {
  const list = Array.isArray(currencies) ? currencies : [];
  const usdcIdx = list.findIndex((row) => {
    const sym = String(row?.currency ?? '')
      .trim()
      .toUpperCase();
    return sym === 'USDC' || sym === 'USDC.E' || sym === 'USDCE';
  });
  return usdcIdx >= 0 ? usdcIdx : 0;
}

function applyCoboSelection(store, coboDepositOptionsRaw, restored, countryCode) {
  if (restored) {
    store.selectedTransferChainIdx = restored.chainIdx;
    store.transferCurrencies = restored.currencies;
    store.selectedTransferTokenIdx = restored.tokenIdx;
    return;
  }
  const geoDefaults = applyCoboTransferGeoDefaults(coboDepositOptionsRaw, countryCode);
  store.selectedTransferChainIdx = geoDefaults.chainIdx;
  store.transferCurrencies = geoDefaults.currencies;
  store.selectedTransferTokenIdx = geoDefaults.tokenIdx;
}

let rcLoadPromise = null;
let coboLoadPromise = null;
let legacyWalletLoadPromise = null;
let coboLoadRunId = 0;

function currentAuthSession() {
  return {
    generation: getAuthGeneration(),
    userId: String(getUserId() ?? '').trim(),
  };
}

function isAuthSessionCurrent({ generation, userId }) {
  const current = currentAuthSession();
  return current.generation === generation && current.userId === userId && hasAuthSession();
}

export const useDepositOptionsStore = defineStore('depositOptions', {
  state: () => ({
    rcTokensRaw: [],
    rcChainsRaw: [],
    rcLoading: false,
    rcLoaded: false,
    rcError: '',

    depositAccounts: [],
    coboDepositOptionsRaw: [],
    transferCurrencies: [],
    selectedTransferChainIdx: 0,
    selectedTransferTokenIdx: 0,
    coboLoading: false,
    coboLoaded: false,
    coboError: '',

    legacyWalletAddress: '',
    legacyWalletLoading: false,
    legacyWalletLoaded: false,
  }),

  actions: {
    clearRcError() {
      this.rcError = '';
    },

    clearCoboError() {
      this.coboError = '';
    },

    async ensureRcOptions({ force = false } = {}) {
      if (!force && this.rcLoaded) return;
      if (rcLoadPromise) {
        await rcLoadPromise;
        return;
      }

      this.rcLoading = true;
      this.rcError = '';

      rcLoadPromise = (async () => {
        try {
          const [tokens, chains] = await Promise.all([
            withRetry(() => getRcTokens(), { retries: 2, delayMs: 800 }),
            withRetry(() => getRcChains(), { retries: 2, delayMs: 800 }),
          ]);
          this.rcTokensRaw = Array.isArray(tokens)
            ? tokens.filter((item) => item?.enabled !== false)
            : [];
          this.rcChainsRaw = Array.isArray(chains)
            ? chains.filter((item) => item?.enabled !== false)
            : [];
          this.rcLoaded = true;
        } catch (err) {
          this.rcTokensRaw = [];
          this.rcChainsRaw = [];
          this.rcLoaded = false;
          if (isRequestTimeoutLike(err)) {
            console.warn('[deposit-options] rc options timeout after retry', err);
            this.rcError = '';
          } else {
            this.rcError = err?.message || 'Failed to load rc options';
          }
        } finally {
          this.rcLoading = false;
          rcLoadPromise = null;
        }
      })();

      await rcLoadPromise;
    },

    async ensureLegacyWalletAddress({ force = false } = {}) {
      const cached = String(getWalletAddress() ?? '').trim();
      if (!force && this.legacyWalletLoaded && this.legacyWalletAddress) return;
      if (legacyWalletLoadPromise) {
        await legacyWalletLoadPromise;
        return;
      }

      if (cached) {
        this.legacyWalletAddress = cached;
        this.legacyWalletLoaded = true;
        return;
      }

      this.legacyWalletLoading = true;
      legacyWalletLoadPromise = (async () => {
        try {
          const { address } = await fetchWalletDepositBalances();
          const resolved = String(address ?? '').trim();
          if (resolved) {
            setWalletAddress(resolved);
            this.legacyWalletAddress = resolved;
          }
          this.legacyWalletLoaded = Boolean(this.legacyWalletAddress);
        } catch (err) {
          console.warn('[deposit-options] legacy wallet address failed', err);
          this.legacyWalletLoaded = false;
        } finally {
          this.legacyWalletLoading = false;
          legacyWalletLoadPromise = null;
        }
      })();

      await legacyWalletLoadPromise;
    },

    async ensureLegacyOptions({ force = false } = {}) {
      await Promise.all([
        this.ensureRcOptions({ force }),
        this.ensureLegacyWalletAddress({ force }),
      ]);
    },

    async ensureCoboOptions({ force = false, countryCode: presetCountryCode } = {}) {
      if (coboLoadPromise) {
        return coboLoadPromise;
      }
      if (!force && this.coboLoaded) return;

      this.coboLoading = true;
      this.coboError = '';
      const session = currentAuthSession();
      const runId = ++coboLoadRunId;

      const task = (async () => {
        try {
          const geoPromise =
            presetCountryCode !== undefined
              ? Promise.resolve({ countryCode: presetCountryCode ?? null })
              : resolveDepositGeoSession().catch(() => ({ countryCode: null }));

          const [accountRes, supportedRes, geoSession] = await Promise.all([
            withRetry(() => coboAccount(), { retries: 2, delayMs: 800 }),
            withRetry(() => coboDepositSupported(), { retries: 2, delayMs: 800 }),
            geoPromise,
          ]);
          if (!isAuthSessionCurrent(session)) return;
          this.depositAccounts = pickDepositAccounts(accountRes);
          this.coboDepositOptionsRaw = pickDepositOptions(supportedRes);
          if (!this.coboDepositOptionsRaw.length) {
            this.coboLoaded = false;
            this.coboError = 'Failed to load cobo deposit options';
            return;
          }

          const manualPick = readManualCoboPick();
          const restored = manualPick
            ? resolveCoboPickFromOptions(this.coboDepositOptionsRaw, manualPick)
            : null;
          applyCoboSelection(this, this.coboDepositOptionsRaw, restored, geoSession.countryCode);
          this.coboLoaded = true;
        } catch (err) {
          if (!isAuthSessionCurrent(session)) return;
          this.depositAccounts = [];
          this.coboDepositOptionsRaw = [];
          this.transferCurrencies = [];
          this.coboLoaded = false;
          if (isRequestTimeoutLike(err)) {
            console.warn('[deposit-options] cobo options timeout after retry', err);
            this.coboError = '';
          } else {
            this.coboError = err?.message || 'Failed to load cobo deposit options';
          }
        } finally {
          if (coboLoadRunId === runId) {
            this.coboLoading = false;
            coboLoadPromise = null;
          }
        }
      })();

      coboLoadPromise = task;
      await task;
    },

    async prefetchForSession({ countryCode } = {}) {
      if (!hasAuthSession()) return;
      const coboEnabled =
        String(process.env.VUE_APP_ENABLE_COBO_DEPOSIT ?? 'true').toLowerCase() !== 'false';
      if (coboEnabled) {
        await this.ensureCoboOptions({ countryCode });
        return;
      }
      await this.ensureLegacyOptions();
    },

    selectCoboTransferToken(idx) {
      this.selectedTransferTokenIdx = idx;
      this.persistManualCoboPick(this.selectedTransferChainIdx, idx);
    },

    selectCoboTransferChain(idx) {
      this.transferCurrencies = this.coboDepositOptionsRaw[idx]?.currencies ?? [];
      this.selectedTransferTokenIdx = pickPreferredTransferCurrencyIdx(this.transferCurrencies);
      this.selectedTransferChainIdx = idx;
      this.persistManualCoboPick(idx, this.selectedTransferTokenIdx);
    },

    persistManualCoboPick(chainIdx, tokenIdx) {
      const chain = this.coboDepositOptionsRaw[chainIdx]?.chain;
      const currency = this.coboDepositOptionsRaw[chainIdx]?.currencies?.[tokenIdx]?.currency;
      if (!chain) return;
      writeManualCoboPick({ chain, currency });
    },

    reset() {
      this.rcTokensRaw = [];
      this.rcChainsRaw = [];
      this.rcLoading = false;
      this.rcLoaded = false;
      this.rcError = '';
      this.depositAccounts = [];
      this.coboDepositOptionsRaw = [];
      this.transferCurrencies = [];
      this.selectedTransferChainIdx = 0;
      this.selectedTransferTokenIdx = 0;
      this.coboLoading = false;
      this.coboLoaded = false;
      this.coboError = '';
      this.legacyWalletAddress = '';
      this.legacyWalletLoading = false;
      this.legacyWalletLoaded = false;
      rcLoadPromise = null;
      coboLoadPromise = null;
      coboLoadRunId += 1;
      legacyWalletLoadPromise = null;
    },
  },
});

export { pickPreferredTransferCurrencyIdx };
